import { useEffect, useRef } from "react";

export default function useScreenWakeLock(active) {
  const wakeLockRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const releaseWakeLock = async () => {
      const lock = wakeLockRef.current;
      wakeLockRef.current = null;
      if (!lock) return;

      try {
        await lock.release();
      } catch (err) {
        console.warn("Screen wake lock release failed:", err);
      }
    };

    const requestWakeLock = async () => {
      if (!active) return;
      if (typeof navigator === "undefined" || !navigator.wakeLock) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (wakeLockRef.current) return;

      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled || !active) {
          try {
            await lock.release();
          } catch (_) {}
          return;
        }

        wakeLockRef.current = lock;
        lock.addEventListener?.("release", () => {
          if (wakeLockRef.current === lock) {
            wakeLockRef.current = null;
          }
        });
      } catch (err) {
        console.warn("Screen wake lock unavailable:", err);
      }
    };

    const handleVisibilityChange = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible" && active) {
        requestWakeLock();
      }
    };

    if (active) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      cancelled = true;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      releaseWakeLock();
    };
  }, [active]);
}
